import * as Response from './challenge.response';
import { AscFile } from '../common-types';
import { AscBadgeColor } from '../skill';
import { BatchType } from '../admin';

export type ChallengeFeedbackStatus = 'red' | 'green' | 'yellow';

export enum ChallengeStatus {
  NOT_STARTED = 'NOT_STARTED',
  COMPLETED = 'COMPLETED',
  PENDING = 'PENDING',
  FAILED = 'FAILED',
  STARTED = 'STARTED',
}

export type ChallengeState =
  | 'not-started'
  | 'completed'
  | 'in-progress'
  | 'wrong'
  | 'pending';

export interface SkillChallenges {
  id: string;
  title: string;
  color?: AscBadgeColor;
  challengeCollections: ChallengeCollection[];
  writingChallenges?: ChallengeItem[];
}

export interface ChallengeCollection {
  id: string;
  title: string;
  locked?: boolean;
  date?: string;
  mainChallengeId?: string;
  mainChallengeCompleted?: boolean;
  totalChallenges: number;
  completedChallenges?: number;
  completedDate?: string;
  skillColor?: AscBadgeColor;
  challenges?: ChallengeItem[];
  coverImageUrl?: string;
  skillId?: string;
  final?: boolean;
}

export interface ChallengeItem {
  id: string;
  title: string;
  hasNewFeedback?: boolean;
  unreadNotificationIds?: string[];
  feedbackCount?: number;
  state?: ChallengeState;
  completedDate?: string;
  estimationInHours?: number;
  deadlineDate?: string;
  coverImageUrl?: string;
  description?: string;
  promptText?: string;
  badgeId?: string;
  coinRewards?: number;
  pointRewards?: number;
  mainChallenge?: boolean;
  locked?: boolean;
  isWritingCompetition?: boolean;
  isWritingChallengeJoined?: boolean;
  writingEventId?: string;
  skillName?: string;
  badge?: {
    location: string;
  };
  participatingStudents?: {
    id: string;
    name?: string;
    avatarUrl?: string;
  }[];
  submission?: {
    response: string;
    files: AscFile[];
  };
}

export enum ChallengeSubmissionListItemKeys {
  age = 'age',
  id = 'id',
  name = 'name',
  createdAt = 'createdAt',
  challengeTitle = 'challengeTitle',
  challenge = 'challenge',
  email = 'email',
  school = 'school',
  points = 'points',
  place = 'place',
  score = 'score',
  studentId = 'studentId',
}

export interface ChallengeSubmissionListItem {
  [ChallengeSubmissionListItemKeys.age]: number;
  [ChallengeSubmissionListItemKeys.id]: string;
  [ChallengeSubmissionListItemKeys.name]: string;
  [ChallengeSubmissionListItemKeys.createdAt]: string;
  [ChallengeSubmissionListItemKeys.challenge]: string;
  [ChallengeSubmissionListItemKeys.challengeTitle]: string;
  [ChallengeSubmissionListItemKeys.email]: string;
  [ChallengeSubmissionListItemKeys.school]: string;
  [ChallengeSubmissionListItemKeys.place]: number;
  [ChallengeSubmissionListItemKeys.points]: number;
  [ChallengeSubmissionListItemKeys.score]: number;
  [ChallengeSubmissionListItemKeys.studentId]: string;
}

export interface ChallengeSubmission {
  id: string;
  createdAt: string;
  updatedAt: string;
  body: string;
  challengeId: string;
  studentId: string;
  jurnalNote: string;
  status: 'ACTIVE' | 'IN_ACTIVE';
}

export enum ChallengeSubmissionTab {
  CHALLENGES = 'challenges',
  SUBMISSIONS = 'submissions',
}

export interface IChallengePastWinner {
  id: string;
  name: string;
  birthDate: string;
  school: string;
  placement: number;
  country: string;
  flag?: string;
  image: string;
  score: number;
}

export type ChallengeFeedbackSubmissionStatusMapped =
  Response.ChallengeFeedbackSubmissionStatusStats & {
    id: string;
    name: string;
    school: string;
  };

export type ChallengeFeedbackChallengeTableType = {
  id: string;
  name: string;
  completedChallenges: number;
  feedbackMissing: number;
  feedbackByAnother: number;
  feedbackByMe: number;
};

export type ChallengeSubmissionMapped = {
  id: string;
  createdAt: string;
  body: string;
  challenge: {
    id: string;
    title: string;
    description: string;
  };
  skill: {
    id: string;
    name: string;
    badgeColor: AscBadgeColor;
  };
  pillar: {
    id: string;
    name: string;
  };
  module: {
    id: string;
    name: string;
  };
  feedbackStatus: ChallengeFeedbackStatus[];
};

export type ChallengeSubmissionsDetailsMapped = {
  student: {
    id: string;
    name: string;
    picture: string;
    batches: BatchType[];
  };
  challengesCount: number;
  submissionsCount: number;
  missingFeedback: number;
  submissions: ChallengeSubmissionMapped[];
};

export type ChallengeFeedbackMapped = {
  myFeedback: Response.ChallengeFeedbackResponse;
  otherFeedbacks: Response.ChallengeFeedbackResponse[];
};

export interface ChallengeSubmissionFile {
  id: string;
  createdAt: string;
  updatedAt: string;
  key: string;
  bucket: string;
  location: string;
  fileName: string;
  mimeType: string;
  fileSize: number;
  takenChallengeId: string;
  studentId: string;
}
